import {useInfiniteQuery, useQueryClient} from '@tanstack/react-query';
import {useCallback, useMemo} from 'react';
import {FilteredItemsListContents, UseGetPaginatedItemsParams} from './types';

export function useGetPaginatedItemsReactQuery<T>({
	queryKey,
	queryFn,
	queryOptions,
	additionalParams = {},
}: UseGetPaginatedItemsParams<T>) {
	const queryClient = useQueryClient();

	const query = useInfiniteQuery({
		queryKey: [...queryKey, additionalParams],
		queryFn: ({pageParam, signal}: {pageParam: unknown; signal?: AbortSignal}) =>
			queryFn({page: pageParam as number, signal, ...additionalParams}),
		initialPageParam: 1,
		getNextPageParam: (lastPage, allPages) => {
			const currentPage = allPages.length;
			const totalPages = lastPage.pages;
			return currentPage < totalPages ? currentPage + 1 : undefined;
		},
		...queryOptions,
	});

	const {data, isSuccess, isLoading, isError, isFetchingNextPage, hasNextPage, fetchNextPage} =
		query;

	const items = useMemo(() => {
		const pages = data?.pages as FilteredItemsListContents<T>[] | undefined;
		return pages?.flatMap(page => page.items) ?? [];
	}, [data?.pages]);

	const itemsListHasValue = isSuccess && !!data;
	const pages = data?.pages as FilteredItemsListContents<T>[] | undefined;
	const firstPageData = pages?.[0];
	const totalPages = firstPageData?.pages ?? 0;
	const totalItems = firstPageData?.total ?? 0;
	const loadingItemsList = isLoading || isFetchingNextPage;
	const itemsListHasError = isError;
	const itemsListIsEmpty = itemsListHasValue && items.length === 0;
	const isLoadingMore = isFetchingNextPage;

	const otherData = useMemo(() => {
		if (!firstPageData) return undefined;

		return Object.keys(firstPageData).reduce(
			(acc, key) => {
				if (!['pages', 'items', 'total'].includes(key)) {
					acc[key] = firstPageData[key];
				}
				return acc;
			},
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			{} as Record<string, any>,
		);
	}, [firstPageData]);

	const loadMore = useCallback(() => {
		if (!hasNextPage || isLoadingMore) return;
		fetchNextPage();
	}, [hasNextPage, isLoadingMore, fetchNextPage]);

	const refresh = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: [...queryKey, additionalParams],
		});
	}, [queryClient, queryKey, additionalParams]);

	const resetState = useCallback(() => {
		queryClient.removeQueries({
			queryKey: [...queryKey, additionalParams],
		});
	}, [queryClient, queryKey, additionalParams]);

	return {
		items,
		otherData,
		loadingItemsList,
		itemsListIsEmpty,
		itemsListHasError,
		isLoading,
		isFetchingNextPage,
		isLoadingMore,
		hasNextPage,
		loadMore,
		refresh,
		resetState,
		totalItems,
		totalPages,
	};
}
